import {
  Button,
  Row,
  Modal as AntModal,
  Form,
  Input,
  Select,
  ColProps,
  RowProps,
  Col,
  Typography,
  message,
  SelectProps,
  Tag,
  Switch,
} from "antd";
import CompanyTable from "./components/CompanyTable";
import { SearchCompany } from "./components/SearchCompany";
import { PlusCircleOutlined } from "@ant-design/icons";
import { useEffect, useState } from "react";
import { generateErrorMessage } from "src/utils/error-message";
import { companyStore } from "src/stores/company-store";
import { useShallow } from "zustand/react/shallow";
import { useDidMount } from "src/hook/useDidMount";
import { organizations, regions } from "src/entity/company/company.constants";
import { debounce, omit, pick, uniq, uniqueId } from "lodash";
import { CreateMappingCompanyDto, GroupCompanyIdArea, MapCompanyIdArea } from "src/entity/company/company-response.entity";
import { ResponseCompanyMapping } from "src/entity/company/company-mapping.entity";
import VocsModal from "src/components/Modal";
import { profileStore } from "src/stores/profile-store";
import { CommonSuccess } from "src/entity/common/menus-enum.enum";
import { NormalError } from "src/entity/common/bad-request.enum";
import { CreateArea, ResponseArea } from "src/entity/company/areas.entity";
import { createAreas } from "src/services/master.service";
import { AxiosError } from "axios";
import ModalTitle from "src/components/AntdCustomPart/ModalTitle/ModalTitle";
import FormLabel from "src/components/AntdCustomPart/FormLabel/FormLabel";
import GroupCompanyArea from "src/components/AntdCustomPart/GroupCompanyArea/GroupCompanyArea";
import useNavigationMenu from "src/hook/useNavigationMenu";
import { canEditTable } from "./components/PermissionCheck/PermissionCheck";
import { COMPANY_GROUP } from "src/entity/common/common.constant";

const { Text } = Typography;
const rowProps: RowProps = {
  gutter: {
    xs: 0,
    md: 12,
  },
};

const colProps: ColProps = {
  xs: { span: 24 },
  md: { span: 12 },
};

const SettingCompany = () => {
  const [messageApi, contextHolder] = message.useMessage();
  const [visibleModal, setVisibleModal] = useState<boolean>(false);
  const [companyForm] = Form.useForm();
  const [manageMapCompany, setManageMapCompany] = useState<MapCompanyIdArea[]>([]);
  const [textSearch, setTextSearch] = useState("");
  const [filterRegion, setFilterRegion] = useState<string[]>([]);
  const [filterOrganization, setFilterOrganization] = useState<string[]>([]);
  const [filterCompanyGroup, setFilterCompanyGroup] = useState<string[]>([]);
  const [filterCompany, setFilterCompany] = useState<string[]>([]);
  const [stateDataAreas, setStateDataAreas] = useState<SelectProps["options"]>([]);
  const [page, setPage] = useState(1);
  const { permissions } = useNavigationMenu();

  const [isEditing, setIsEditing] = useState("");
  const [reportVisible, setReportVisible] = useState<{ companyGroup: string; isActive: boolean }[]>([
    { companyGroup: COMPANY_GROUP.SCGC_SUBSIDIARY, isActive: false },
    { companyGroup: COMPANY_GROUP.SCGC_JV, isActive: false },
    { companyGroup: COMPANY_GROUP.SCGC_ALL, isActive: false },
  ]);

  const profile = profileStore(useShallow((state) => state.profile));
  const companies = profile?.company ?? [];
  const getAllCompanyGroup = companyStore(useShallow((state) => state.getAllCompanyGroup));
  const dataCompanyGroup = companyStore(useShallow((state) => state.dataCompanyGroup));
  const getAllArea = companyStore(useShallow((state) => state.getAllArea));
  const dataArea = companyStore(useShallow((state) => state.dataArea));
  const createCompanyMapping = companyStore(useShallow((state) => state.createCompanyMap));
  const updateMappingCompany = companyStore(useShallow((state) => state.updateMappingCompany));
  const setLoadingManageCompanyMap = companyStore(useShallow((state) => state.setLoadingManageCompanyMap));
  const clearErrorSettingCompany = companyStore(useShallow((state) => state.clearErrorSettingCompany));
  const errorManageMapCompany = companyStore(useShallow((state) => state.error));
  const loadingManageCompanyMap = companyStore(useShallow((state) => state.loadingManageCompanyMap));
  const searchMappingCompany = companyStore(useShallow((state) => state.searchMappingCompany));
  const dataMapCompany = companyStore(useShallow((state) => state.dataMapCompany));
  const loadingSearch = companyStore(useShallow((state) => state.loadingSearch));
  const deleteMappingCompany = companyStore(useShallow((state) => state.deleteMappingCompany));

  useDidMount(() => {
    getAllCompanyGroup();
    getAllArea();
    searchMappingCompany({});
    addListMapCompanyArea();
  });

  useEffect(() => {
    if (dataArea && dataArea?.length) {
      const newAreasOption = dataArea.map((item) => ({
        value: item._id ? JSON.stringify(item) : item.areaValue,
        label: item.areaName,
      }));
      setStateDataAreas(newAreasOption);
    }
  }, [dataArea]);

  const onCloseModal = () => {
    companyForm.resetFields();
    const uniqKey = uniqueId("map-company-area-");
    setManageMapCompany([{ key: uniqKey, [`companyId`]: null, [`area`]: null }]);
    clearErrorSettingCompany();
    setIsEditing("");
    setReportVisible([
      { companyGroup: COMPANY_GROUP.SCGC_SUBSIDIARY, isActive: false },
      { companyGroup: COMPANY_GROUP.SCGC_JV, isActive: false },
      { companyGroup: COMPANY_GROUP.SCGC_ALL, isActive: false },
    ]);
    // Reset form values for reportVisible
    companyForm.setFieldsValue({
      [`reportVisible_${COMPANY_GROUP.SCGC_SUBSIDIARY}`]: false,
      [`reportVisible_${COMPANY_GROUP.SCGC_JV}`]: false,
      [`reportVisible_${COMPANY_GROUP.SCGC_ALL}`]: false,
    });
    setVisibleModal(false);
  };

  const onOpenModal = () => {
    setVisibleModal(true);
  };

  const onClickEdit = (record: ResponseCompanyMapping) => {
    setIsEditing(record._id);
    const tmpManageMapCompanyArea: MapCompanyIdArea[] = [];
    if (record.areaCompany?.length) {
      record.areaCompany.forEach((item) => {
        const uniqKey = uniqueId("map-company-area-");
        const mapArea = item.area.map((e) => JSON.stringify(e));
        tmpManageMapCompanyArea.push({ key: uniqKey, companyId: item.companyId, area: mapArea });
        companyForm.setFieldsValue({
          [`${uniqKey}-companyId`]: item.companyId,
          [`${uniqKey}-areaId`]: mapArea,
        });
      });
      companyForm.setFieldsValue({
        region: record.region,
        organization: record.organization,
        companyGroupId: record.companyGroupId._id,
        company: record.company._id,
      });
    }
    console.log({ record });
    if (record?.reportVisible?.length) {
      setReportVisible(record.reportVisible);
      // Set form values for reportVisible
      const reportVisibleFormValues: { [key: string]: boolean } = {};
      record.reportVisible.forEach((item) => {
        reportVisibleFormValues[`reportVisible_${item.companyGroup}`] = item.isActive;
      });
      companyForm.setFieldsValue(reportVisibleFormValues);
    } else {
      setReportVisible([
        { companyGroup: COMPANY_GROUP.SCGC_SUBSIDIARY, isActive: false },
        { companyGroup: COMPANY_GROUP.SCGC_JV, isActive: false },
        { companyGroup: COMPANY_GROUP.SCGC_ALL, isActive: false },
      ]);
      // Set default form values for reportVisible
      companyForm.setFieldsValue({
        [`reportVisible_${COMPANY_GROUP.SCGC_SUBSIDIARY}`]: false,
        [`reportVisible_${COMPANY_GROUP.SCGC_JV}`]: false,
        [`reportVisible_${COMPANY_GROUP.SCGC_ALL}`]: false,
      });
    }
    setManageMapCompany(tmpManageMapCompanyArea);
    onOpenModal();
  };

  const addListMapCompanyArea = () => {
    const tmpManageMapCompanyArea = [...manageMapCompany];
    const uniqKey = uniqueId("map-company-area-");
    tmpManageMapCompanyArea.push({ key: uniqKey, [`companyId`]: null, [`area`]: null });
    setManageMapCompany(tmpManageMapCompanyArea);
  };

  const manageMapCompanyArea = (key?: string) => {
    if (!key) {
      addListMapCompanyArea();
    } else {
      const tmpManageMapCompanyArea = [...manageMapCompany];
      // splice here !
      const removeIndex = tmpManageMapCompanyArea.findIndex((e) => e.key === key);
      tmpManageMapCompanyArea.splice(removeIndex, 1);
      setManageMapCompany(tmpManageMapCompanyArea);
    }
    validateMapCompanyForm();
  };

  const onUpdateKeyVal = async (key: string, field: keyof MapCompanyIdArea, value: string | boolean) => {
    const tmpManageMapCompany = [...manageMapCompany];
    const targetIndex = tmpManageMapCompany.findIndex((item) => item.key === key);
    if (targetIndex !== -1) {
      tmpManageMapCompany[targetIndex][field] = value as never;
    }
    // console.log(tmpManageMapCompany);
    setManageMapCompany(tmpManageMapCompany);
  };

  const handleInputChange = async (key: string, field: keyof MapCompanyIdArea, value: string | boolean) => {
    const tmpManageMapCompany = [...manageMapCompany];
    const targetIndex = tmpManageMapCompany.findIndex((item) => item.key === key);
    if (targetIndex !== -1) {
      tmpManageMapCompany[targetIndex][field] = value as never;
    }
    // console.log(tmpManageMapCompany);
    setManageMapCompany(tmpManageMapCompany);
    validateMapCompanyForm();
  };
  const debounceHandleInput = debounce(handleInputChange, 500);

  const handleReportVisibleChange = (companyGroup: string, isActive: boolean) => {
    setReportVisible((prev) => prev.map((item) => (item.companyGroup === companyGroup ? { ...item, isActive } : item)));
    // Update form value
    companyForm.setFieldsValue({
      [`reportVisible_${companyGroup}`]: isActive,
    });
  };

  const validateMapCompanyForm = async () => {
    await companyForm.validateFields().catch(() => console.log());
  };

  const validateCompanyArea = (_: any, value: any) => {
    if (manageMapCompany.filter((item) => `${item.companyId}` === value).length > 1) {
      return Promise.reject("Duplicate companyID.");
    } else {
      return Promise.resolve();
    }
  };

  const generateErrorAreaApi = (error: AxiosError<{ message: any }>) => {
    let message = "Something went wrong (area API).";

    if (error?.response?.data && error.response.data?.message && Array.isArray(error.response.data.message)) {
      const { response } = error;
      const { data } = response;
      const { message: eMessage } = data;
      message = eMessage[0] && eMessage[0].includes("between 1 and") ? eMessage[0].slice(7) : eMessage[0];
    }

    return message;
  };

  const processCreateArea = async (areasList: string[]): Promise<string[] | string> => {
    const tmpAreaList = [...areasList];
    const listAreasId: string[] = [];
    const listCreateNewArea: CreateArea[] = [];
    let checkErrorCreateArea = "no";
    tmpAreaList.forEach((e: string) => {
      try {
        const tmpObjArea: ResponseArea = JSON.parse(e);

        if (tmpObjArea?._id) {
          listAreasId.push(tmpObjArea._id);
        } else throw new Error();
      } catch (error) {
        // Case 1: Check duplicate with create new list.
        const findDuplicateCreate = listCreateNewArea.filter(
          (item) => e.toLowerCase().trim() == item.areaValue.toLowerCase() && e.toLowerCase().trim() == item.areaName.toLowerCase(),
        );

        // Case 2: Check Duplicate with area origin list.
        const originAreaList: ResponseArea[] = [...dataArea];
        const findDuplicateWithOrigin = originAreaList.filter(
          (item) => e.toLowerCase().trim() == item.areaValue.toLowerCase() && e.toLowerCase().trim() == item.areaValue.toLowerCase(),
        );

        if (!findDuplicateCreate?.length && !findDuplicateWithOrigin?.length) {
          const newObjArea = {
            areaName: e.trim(),
            areaValue: e.trim(),
          };
          listCreateNewArea.push(newObjArea);
        }

        if (findDuplicateWithOrigin?.length) listAreasId.push(...findDuplicateWithOrigin.map((e) => e._id));
      }
    });

    // console.log("List create new : ", listCreateNewArea);

    if (listCreateNewArea?.length) {
      // setLoadingCreateSkill(true);
      const responseCreateAreas = await createAreas({ data: listCreateNewArea });
      // console.log(`🚀  ->  responseCreateAreas:`, responseCreateAreas);

      if (!responseCreateAreas?.data?.data) {
        const messageErr = generateErrorAreaApi(responseCreateAreas as any);
        checkErrorCreateArea = "yes";
        messageApi.open({
          type: "error",
          content: messageErr,
        });
        setLoadingManageCompanyMap(false);
        return checkErrorCreateArea;
      }

      const tmpResponse = responseCreateAreas.data.data || [];
      tmpResponse.map((e: ResponseArea) => listAreasId.push(e._id));
    }

    return uniq(listAreasId);
  };

  const submitForm = async () => {
    let checkError = false;
    await companyForm.validateFields().catch((err) => {
      checkError = true;
    });

    if (checkError) return;
    const formData = companyForm.getFieldsValue();

    // // Extract reportVisible data from form
    const reportVisibleFromForm = reportVisible.map((item) => ({
      companyGroup: item.companyGroup,
      isActive: formData[`reportVisible_${item.companyGroup}`] || false,
    }));

    const finalData: CreateMappingCompanyDto = {
      ...pick(formData, ["region", "organization", "companyGroupId", "company"]),
      areaCompany: manageMapCompany ? (manageMapCompany.map((item) => ({ ...omit(item, ["key"]) })) as GroupCompanyIdArea[]) : [],
      reportVisible: reportVisibleFromForm,
    };

    // console.log("Final data with reportVisible : ", finalData);

    let hasErrorAreaApi = false;
    setLoadingManageCompanyMap(true);
    for (const areaCompany of finalData.areaCompany) {
      const tmpAreas: string[] = [...areaCompany.area];
      const responseProcessArea = await processCreateArea(tmpAreas);
      // console.log("responseProcessArea :: ", responseProcessArea);
      if (Array.isArray(responseProcessArea)) areaCompany.area = responseProcessArea;
      else {
        hasErrorAreaApi = true;
        break;
      }
    }
    // console.log("Final data : ", finalData);
    if (hasErrorAreaApi) return;
    if (isEditing) {
      updateMappingCompany({ ...finalData, _id: isEditing }).then(fetchAfterActionMap);
    } else createCompanyMapping(finalData).then(fetchAfterActionMap);
  };

  const fetchAfterActionMap = (message?: unknown) => {
    if (message) {
      if (typeof message === "string" && message.includes(NormalError.DuplicateSettingCompany))
        companyForm.setFields([
          {
            name: "company",
            errors: [""],
          },
        ]);
      else return;
    } else {
      getAllArea();
      messageApi.open({
        type: "success",
        content: CommonSuccess.SAVED_SUCCESSFULL,
      });
      searchMappingCompany({
        textSearch,
        regions: filterRegion,
        organizations: filterOrganization,
        companyGroupId: filterCompanyGroup,
        company: filterCompany,
      });
      onCloseModal();
    }
  };

  const removeMapping = async (id: string) => {
    const confirm = await VocsModal.delete({
      message: "",
    });

    if (!confirm) return;

    deleteMappingCompany(id).then(() => fetchAfterActionMap());
  };

  // console.log("Manage CompanyState : ", manageMapCompany);
  return (
    <Col span={24}>
      <Row className="w-full" justify={"space-between"}>
        <div></div>
        <SearchCompany
          action={searchMappingCompany}
          dataRegion={regions}
          dataOrganization={organizations}
          dataCompanyGroup={dataCompanyGroup}
          dataArea={dataArea}
          dataCompanies={companies}
          placeholder="search text"
          textSearch={textSearch}
          setTextSearch={setTextSearch}
          filterRegion={filterRegion}
          setFilterRegion={setFilterRegion}
          filterOrganization={filterOrganization}
          setFilterOrganization={setFilterOrganization}
          filterCompanyGroup={filterCompanyGroup}
          setFilterCompanyGroup={setFilterCompanyGroup}
          filterCompany={filterCompany}
          setFilterCompany={setFilterCompany}
          setPage={setPage}
        />

        <Button
          type="primary"
          size={"middle"}
          className="flex items-center"
          onClick={onOpenModal}
          style={{ visibility: canEditTable(permissions) ? "visible" : "hidden" }}
          disabled={!canEditTable(permissions)}
        >
          <PlusCircleOutlined />
          New
        </Button>
      </Row>
      <Row className="w-full bg-white mt-4">
        {contextHolder}

        <CompanyTable
          canEditTable={canEditTable(permissions)}
          loadingSearch={loadingSearch}
          data={dataMapCompany}
          onClickEdit={onClickEdit}
          deleteMappingCompany={removeMapping}
          loadingManageCompanyMap={loadingManageCompanyMap}
          page={page}
          setPage={setPage}
        />

        <AntModal
          title={<ModalTitle title="Company" />}
          open={visibleModal}
          width={1000}
          styles={{ body: { maxHeight: "60vh", overflowY: "scroll", overflowX: "hidden" } }}
          onCancel={onCloseModal}
          footer={
            <Row className="w-full border-t" justify={"end"}>
              <Button
                className="mt-4"
                disabled={manageMapCompany.length === 0 ? true : false}
                key="submit"
                type="primary"
                loading={loadingManageCompanyMap}
                onClick={submitForm}
              >
                Save
              </Button>
            </Row>
          }
          maskClosable={false}
          centered
        >
          {errorManageMapCompany && (
            <Row className="w-full" justify={"center"}>
              <Text type="danger">{errorManageMapCompany}</Text>
            </Row>
          )}
          <Form
            form={companyForm}
            name="setting-company"
            layout={"vertical"}
            requiredMark={false}
            onFieldsChange={(_) => {
              if (errorManageMapCompany) {
                clearErrorSettingCompany();
                companyForm.setFields([
                  {
                    name: "company",
                    errors: undefined,
                  },
                ]);
              }
            }}
          >
            <Row {...rowProps}>
              <Col {...colProps}>
                <Form.Item
                  name="region"
                  label={<FormLabel label={"Region"} required />}
                  rules={[
                    {
                      required: true,
                      message: generateErrorMessage("Region"),
                    },
                  ]}
                >
                  <Select>
                    {regions.map((item, index) => (
                      <Select.Option key={"region" + index} value={item.value}>
                        {item.label}
                      </Select.Option>
                    ))}
                  </Select>
                </Form.Item>
              </Col>

              <Col {...colProps}>
                <Form.Item
                  name="organization"
                  label={<FormLabel label={"Organization Type"} required />}
                  rules={[
                    {
                      required: true,
                      message: generateErrorMessage("Organization Type"),
                    },
                  ]}
                >
                  <Select>
                    {organizations.map((item, index) => (
                      <Select.Option key={"organization" + index} value={item.value}>
                        {item.label}
                      </Select.Option>
                    ))}
                  </Select>
                </Form.Item>
              </Col>

              <Col {...colProps}>
                <Form.Item
                  name="companyGroupId"
                  label={<FormLabel label={"Company Group"} required />}
                  rules={[
                    {
                      required: true,
                      message: generateErrorMessage("Company Group"),
                    },
                  ]}
                >
                  <Select>
                    {dataCompanyGroup.map((item, index) => (
                      <Select.Option key={"companyGroupId" + index} value={item._id}>
                        {item.companyGroupName}
                      </Select.Option>
                    ))}
                  </Select>
                </Form.Item>
              </Col>

              <Col {...colProps}>
                <Form.Item
                  name="company"
                  label={<FormLabel label={"Company"} required />}
                  rules={[
                    {
                      required: true,
                      message: generateErrorMessage("Company"),
                    },
                  ]}
                >
                  <Select>
                    {companies.map((item, index) => (
                      <Select.Option key={"company" + index} value={item._id}>
                        {item.companyName}
                      </Select.Option>
                    ))}
                  </Select>
                </Form.Item>
              </Col>
            </Row>

            <Row>
              <Col span={24}>
                <Form.Item label={<FormLabel label={"Report Visibility"} />} style={{ marginBottom: 0 }}>
                  <Row gutter={[16, 16]}>
                    {reportVisible.map((item, index) => (
                      <Col span={8} key={index}>
                        <Form.Item name={`reportVisible_${item.companyGroup}`} valuePropName="checked" style={{ marginBottom: 0 }}>
                          <div style={{ display: "flex", alignItems: "center", gap: "8px", justifyContent: "center" }}>
                            <Text>{item.companyGroup}</Text>
                            <Switch
                              checked={item.isActive}
                              onChange={(checked) => handleReportVisibleChange(item.companyGroup, checked)}
                              checkedChildren="Active"
                              unCheckedChildren="Inactive"
                            />
                          </div>
                        </Form.Item>
                      </Col>
                    ))}
                  </Row>
                </Form.Item>
              </Col>
            </Row>

            <Row {...rowProps}>
              {manageMapCompany.map((item, index) => {
                return (
                  <GroupCompanyArea
                    key={`company-group-area-${index}`}
                    index={index}
                    item={item}
                    validateCompanyArea={validateCompanyArea}
                    debounceHandleInput={debounceHandleInput}
                    onUpdateKeyVal={onUpdateKeyVal}
                    stateDataAreas={stateDataAreas}
                    manageMapCompanyArea={manageMapCompanyArea}
                    companyForm={companyForm}
                  />
                );
              })}
            </Row>
          </Form>
          <Row justify={"end"} className="-mt-2 pb-2">
            <Button type="primary" size={"middle"} className="flex items-center" onClick={() => manageMapCompanyArea()}>
              <PlusCircleOutlined />
            </Button>
          </Row>
        </AntModal>
      </Row>
    </Col>
  );
};

export default SettingCompany;
